/**
 * Page base module.
 *
 */

import { VuexModule, Mutation, Action } from 'vuex-module-decorators';

import { Page, PageInterface, SortItem } from '@/types/page/page';
import { TableApiInterface, TableInterface, TableTitles } from '@/types/page/table';
import PageEntity from '@/store/pageEntity';
import { PageSort } from '@/types/page/page';

import ResponseHandlerModule from '@/store/modules/responseHandler';

export default abstract class PageBaseModule extends VuexModule implements Page {
    pageSettings: PageInterface;
    isGlobalError = false;

    protected constructor(module: PageBaseModule) {
        super(module);
        const page = new PageEntity();
        this.pageSettings = page.values;
    }

    @Mutation
    UPDATE_TABLE_TITLES(titles: TableTitles) {
        (this.pageSettings.table as TableInterface).titles = titles;
    }

    @Mutation
    UPDATE_HIDDEN_COLUMNS(columns: Record<string, any>) {
        (this.pageSettings.table as TableInterface).hiddenColumns = columns;
    }

    @Mutation
    SET_TABLE(table: TableInterface) {
        this.pageSettings.table = table;
    }

    @Mutation
    CLEAR_TABLE() {
        this.pageSettings.table = {};
    }

    @Mutation
    SET_PAGE(number: number) {
        this.pageSettings.pageCurrent = number;
    }

    @Mutation
    SET_PAGE_AMOUNT_ITEMS(number: number) {
        this.pageSettings.pageAmountItems = number;
    }

    @Mutation
    SET_TITLE_EDIT(title: string) {
        this.pageSettings.titleEdit = title;
    }

    @Mutation
    SET_SORT(sorts: PageSort) {
        this.pageSettings.sort = sorts;

        if (this.pageSettings.moduleName && this.pageSettings.moduleName.length > 0) {
            window.localStorage[this.pageSettings.moduleName] = JSON.stringify(sorts);
        }
    }

    @Mutation
    setGlobalError(isGlobalError: boolean) {
        this.isGlobalError = isGlobalError;
    }

    @Action({ rawError: true })
    async init() {
        await this.context.dispatch('getList');
    }

    @Action({ rawError: true })
    initList(): void {
        this.context.dispatch('getList');
    }

    @Action({ rawError: true })
    getList(): void {
        this.context.dispatch('setList', {} as TableApiInterface);
    }

    @Action({ rawError: true })
    getSortForRequest() {
        let sort = '';
        let index: string;
        let item: SortItem;
        for ([index, item] of Object.entries(this.pageSettings.sort)) {
            sort += `sort[${index}][id]=${item.key}&sort[${index}][value]=${item.sort}&`;
        }

        return sort;
    }

    @Action({ rawError: true })
    initListById(id: string): void {
        this.getListById(id);
    }

    @Action({ rawError: true })
    getListById(id: string): void {
        console.info(id);
    }

    @Action({ rawError: true })
    setList(table: TableApiInterface) {
        const hiddenColumns = Object.values(table.titles)
            .filter(title => title.visible === false)
            .map(title => {
                return title.id;
            });
        const tableInfo = {
            titles: table.titles,
            rows: table.rows,
            sortFields: table.sortFields,
            pagination: table.pagination ? table.pagination : {},
            totalItems: table.totalItems,
            filters: table.filters,
            hiddenColumns: hiddenColumns,
        } as TableInterface;

        this.context.commit('SET_TABLE', tableInfo);
    }

    @Action({ rawError: true })
    clearList() {
        this.context.commit('CLEAR_TABLE');
    }

    @Action({ rawError: true })
    updateTitleEdit(title: string) {
        this.pageSettings.titleEdit = title;
    }

    @Action({ rawError: true })
    updatePage(number: string) {
        this.context.commit('SET_PAGE', parseInt(number));
        this.context.dispatch('getList');
    }

    @Action({ rawError: true })
    async updatePageAmountItems(number: string) {
        await this.context.commit('SET_PAGE', 1);
        await this.context.commit('SET_PAGE_AMOUNT_ITEMS', number);
        await this.context.dispatch('getList');
    }

    @Action({ rawError: true })
    async updatePaginationSettings(params: { moduleName: string; amountItems: number }) {
        await this.context.dispatch('setPageAmountStorageValue', {
            moduleName: params.moduleName,
            number: params.amountItems,
        });
        await this.context.commit('SET_PAGE', 1);
        await this.context.commit('SET_PAGE_AMOUNT_ITEMS', params.amountItems);
    }

    @Action({ rawError: true })
    setPageAmountStorageValue(params: { moduleName: string; number: number }) {
        window.localStorage[`${params.moduleName}_pageAmountItems`] = params.number;
    }

    @Action({ rawError: true })
    getPageAmountStorageValue(moduleName: string) {
        try {
            return Number(window.localStorage[`${moduleName}_pageAmountItems`]) || 50;
        } catch (error: any) {
            ResponseHandlerModule.showNotify({ message: error, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async getItemsQuery(moduleName: string) {
        try {
            const pageAmountItems = await this.context.dispatch('getPageAmountStorageValue', moduleName);

            return pageAmountItems ? `items=${pageAmountItems}&` : '';
        } catch (error: any) {
            ResponseHandlerModule.showNotify({ message: error, type: 'fail' });
        }
    }

    @Action({ rawError: true })
    async sort(params: { field: string; sort: string }) {
        const sortProcessed = await this.context.dispatch('sortProcessed', params);

        this.context.commit('SET_SORT', sortProcessed);
        this.context.dispatch('getList');
    }

    @Action({ rawError: true })
    async delete(id: string | number) { console.log(`delete ${id}`) } // eslint-disable-line

    @Action({ rawError: true })
    sortProcessed(params: { field: string; sort: string }) {
        let sortNew: string;
        if (!params.sort) {
            sortNew = 'ASC';
        } else {
            sortNew = params.sort === 'ASC' ? 'DESC' : 'ASC';
        }

        let sortProcessed: any[] = [];
        const sortExisted = Object.values(this.pageSettings.sort).filter((sort: any) => sort.key === params.field)[0];

        if (sortExisted) {
            Object.values(this.pageSettings.sort).forEach((sortEntity: any) => {
                const sort = sortEntity.key === params.field ? sortNew : sortEntity.sort;

                if (sortEntity.key === params.field && sortExisted.sort === 'DESC') {
                    return;
                }

                sortProcessed.push({ key: sortEntity.key, sort: sort });
            });

            return sortProcessed;
        }

        const index = Object.values(this.pageSettings.sort).length;
        sortProcessed[index] = {
            key: params.field,
            sort: sortNew,
        };
        sortProcessed = { ...this.pageSettings.sort, ...sortProcessed };

        return sortProcessed;
    }

    @Action({ rawError: true })
    clearSort() {
        this.context.commit('SET_SORT', {});
    }
}
